import styled from '@emotion/styled';
import { Flex } from 'antd';
import { type SyntheticEvent, useRef } from 'react';
import { sendAnalyticsEvent } from 'src/lib/googleAnalytics';
import { t } from 'src/lib/player/i18n';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import { IconFail, IconGear } from '../../BasicComponents/Icons';
import { ReloadButton } from '../ReloadButton';
import { CloseSidebarButton } from './CloseSidebarButton';
import { SearchIconButton } from './SearchIconButton';

function preventDefault(event: SyntheticEvent) {
  event.preventDefault();
}

export type SidebarSearchFormProps = {
  searchText: string;
  isSettingsTabActive: boolean;
  onSearchTextChange: (text: string) => void;
  onClose: () => void;
  onSwitchToSearchTab: () => void;
  onToggleSettingsTab: () => void;
  autoReloadEnabled?: boolean;
  isReloading?: boolean;
  onReload?: () => Promise<void>;
};

export function SidebarSearchForm({
  searchText,
  isSettingsTabActive,
  onSearchTextChange,
  onClose,
  onSwitchToSearchTab,
  onToggleSettingsTab,
  autoReloadEnabled = false,
  isReloading = false,
  onReload = async () => {},
}: SidebarSearchFormProps): JSX.Element {
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearchIconClick = () => {
    onSwitchToSearchTab();
    searchInputRef.current?.focus();
  };

  return (
    <Flex>
      <SearchForm onSubmit={preventDefault}>
        <CloseSidebarButton onClick={onClose} />
        <SearchIconButton onClick={handleSearchIconClick} />
        {searchText && <SearchClearIcon onClick={() => onSearchTextChange('')} />}
        <SearchInput
          placeholder={t('Search...')}
          value={searchText}
          ref={searchInputRef}
          onChange={(event) => onSearchTextChange(event.target.value)}
          {...onClickGenerator(onSwitchToSearchTab)}
        />
      </SearchForm>
      <SidebarToolbarButton
        active={isSettingsTabActive}
        title={t('Settings')}
        {...onClickGenerator(onToggleSettingsTab)}
      >
        <IconGear />
      </SidebarToolbarButton>
      <ReloadButton
        autoReloadEnabled={autoReloadEnabled}
        isReloading={isReloading}
        onReload={onReload}
      />
    </Flex>
  );
}

const SidebarToolbarButton = styled.div<{ active?: boolean }>`
  display: flex;
  height: 42px;
  width: 42px;
  justify-content: center;
  align-items: center;
  border: 1px solid var(--base-color-50);
  cursor: pointer;
  font-weight: 600;
  margin: 10px;
  margin-left: 0;
  border-radius: 4px;
  text-align: center;
  color: ${({ active }) => (active ? 'var(--base-color)' : 'black')};
  border-width: ${({ active }) => (active ? '2px' : '1px')};
`;

const SearchForm = styled.form`
  border-radius: 4px;
  border: 1px solid var(--base-color-dark);
  background: white;
  width: auto;
  margin: 10px;
  position: relative;
  flex: 1;

  @media screen and (max-width: 750px) {
    margin-left: 40px;
  }
`;

const SearchClearIcon = ({ onClick }: { onClick: () => void }) => (
  <SearchClearIconWrapper
    {...onClickGenerator(() => {
      sendAnalyticsEvent('starter-list-clear');
      onClick();
    })}
  />
);

const SearchClearIconWrapper = styled(IconFail)`
  position: absolute;
  top: 0;
  right: 0;
  padding: 12px;
  font-size: 16px;
  cursor: pointer;
`;

const SearchInput = styled.input`
  height: 40px;
  line-height: 16px;
  box-shadow: none;
  padding-left: 38px;
  padding-top: 12px;
  padding-bottom: 12px;
  border-radius: 4px;
  width: 100%;
  border: none;
  outline: none;
`;
